import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/inputs/Button";
import Dropdown from "../components/inputs/Dropdown";
import Input from "../components/inputs/Input";
import Badge from "../components/shared/Badge";
import EmptyState from "../components/shared/EmptyState";
import ProgressBar from "../components/shared/ProgressBar";
import { useAuth } from "../hooks/useAuth";
import { panelAPI } from "../services/api";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getPanelTitle = (panel) =>
  panel.panelName || panel.title || `Panel #${panel.id}`;

const getPanelStatus = (panel) => panel.panelStatus || panel.status || "Active";

const getMemberCount = (panel) =>
  panel.members?.length ||
  panel.PanelMembers?.length ||
  panel.panelMembers?.length ||
  0;

const PanelsList = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [panels, setPanels] = useState([]);
  const [filteredPanels, setFilteredPanels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("DATE_DESC");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPanels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = isAuthenticated
        ? await panelAPI.getAllPanels()
        : await panelAPI.getPublicPanels();

      if (response.success) {
        const fetchedPanels = response.panels || [];
        setPanels(fetchedPanels);
        setFilteredPanels(fetchedPanels);
      }
    } catch (err) {
      console.error("Error fetching panels:", err);
      setError("Failed to load panels");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPanels();
  }, [fetchPanels]);

  useEffect(() => {
    let filtered = [...panels];
    const query = searchTerm.trim().toLowerCase();

    if (query) {
      filtered = filtered.filter((panel) => {
        const title = getPanelTitle(panel).toLowerCase();
        const panelId = String(panel.panelId || panel.id || "").toLowerCase();
        const region = String(panel.femaRegion || "").toLowerCase();
        const type = String(panel.panelType || "").toLowerCase();
        return (
          title.includes(query) ||
          panelId.includes(query) ||
          region.includes(query) ||
          type.includes(query)
        );
      });
    }

    if (statusFilter !== "ALL") {
      if (statusFilter === "PUBLISHED") {
        filtered = filtered.filter((panel) => panel.isPublished);
      } else {
        filtered = filtered.filter(
          (panel) => getPanelStatus(panel) === statusFilter
        );
      }
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "DATE_ASC":
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case "DEADLINE":
          return new Date(a.deadline || 0) - new Date(b.deadline || 0);
        case "PROGRESS":
          return (b.progress || 0) - (a.progress || 0);
        case "NAME":
          return getPanelTitle(a).localeCompare(getPanelTitle(b));
        case "DATE_DESC":
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });

    setFilteredPanels(filtered);
  }, [searchTerm, statusFilter, sortBy, panels]);

  const stats = useMemo(() => {
    const active = panels.filter((p) => getPanelStatus(p) === "Active").length;
    const closed = panels.filter((p) => getPanelStatus(p) === "Closed").length;
    const published = panels.filter((p) => p.isPublished).length;
    return { total: panels.length, active, closed, published };
  }, [panels]);

  const getStatusVariant = (status) => {
    switch (status) {
      case "Closed":
      case "COMPLETED":
        return "success";
      case "Active":
      case "IN_PROGRESS":
        return "primary";
      case "PENDING":
        return "warning";
      case "PUBLISHED":
        return "info";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <div className="bg-srp-foam min-h-[calc(100vh-200px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div
            className="flex flex-col items-center justify-center gap-3 text-custom-blue"
            role="status"
            aria-live="polite"
          >
            <div
              className="h-10 w-10 rounded-full border-2 border-custom-blue border-t-transparent animate-spin"
              aria-hidden="true"
            />
            <p className="text-base font-medium">Loading panels…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-srp-foam min-h-[calc(100vh-200px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div
            className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex flex-wrap items-center gap-3"
            role="alert"
          >
            <span>{error}</span>
            <Button variant="outline" size="sm" onClick={fetchPanels}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-srp-foam min-h-[calc(100vh-200px)]">
      <div className="bg-custom-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-custom-cyan-bright mb-2">
            Panel management
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Scientific Resolution Panels
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/85">
            Review active and closed panels working through flood hazard
            determination data.
          </p>

          <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Total", value: stats.total },
              { label: "Active", value: stats.active },
              { label: "Closed", value: stats.closed },
              { label: "Published", value: stats.published },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-white/15 bg-white/10 px-4 py-3"
              >
                <dt className="text-xs uppercase tracking-wide text-white/70">
                  {item.label}
                </dt>
                <dd className="mt-1 text-2xl font-bold">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white border border-custom-blue/10 rounded-xl shadow-sm p-5 sm:p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Search panels"
                type="text"
                name="search"
                value={searchTerm}
                field={{
                  onChange: (e) => setSearchTerm(e.target.value),
                  value: searchTerm,
                }}
                placeholder="Search by name, ID, region, or type…"
              />
            </div>

            <Dropdown
              headLabel="Status"
              label="All statuses"
              options={[
                { value: "ALL", label: "All statuses" },
                { value: "Active", label: "Active" },
                { value: "Closed", label: "Closed" },
                { value: "PUBLISHED", label: "Published" },
              ]}
              value={statusFilter}
              onSelect={setStatusFilter}
            />

            <Dropdown
              headLabel="Sort by"
              label="Newest first"
              options={[
                { value: "DATE_DESC", label: "Newest first" },
                { value: "DATE_ASC", label: "Oldest first" },
                { value: "NAME", label: "Name A-Z" },
                { value: "DEADLINE", label: "Deadline" },
                { value: "PROGRESS", label: "Progress" },
              ]}
              value={sortBy}
              onSelect={setSortBy}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-700">
            <p>
              Showing{" "}
              <span className="font-semibold text-custom-blue">
                {filteredPanels.length}
              </span>{" "}
              of {panels.length} panels
            </p>
            {(searchTerm || statusFilter !== "ALL") && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("ALL");
                }}
                className="font-semibold text-custom-light-blue underline underline-offset-2 hover:text-custom-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue rounded"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {filteredPanels.length > 0 ? (
          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 list-none p-0 m-0">
            {filteredPanels.map((panel) => {
              const title = getPanelTitle(panel);
              const status = getPanelStatus(panel);
              const members = getMemberCount(panel);
              const progress = Number(panel.progress || 0);

              return (
                <li key={panel.id}>
                  <button
                    type="button"
                    className="group h-full w-full text-left bg-white border border-custom-blue/10 rounded-xl shadow-sm hover:shadow-md hover:border-custom-light-blue/40 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue focus-visible:ring-offset-2"
                    onClick={() => navigate(`/panel/${panel.id}`)}
                    aria-label={`Open panel ${title}`}
                  >
                    <div className="h-1.5 rounded-t-xl bg-gradient-to-r from-custom-blue to-custom-light-blue" />
                    <div className="p-5 sm:p-6 flex flex-col h-[calc(100%-0.375rem)]">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-wide text-custom-light-blue">
                            {panel.panelId || `ID ${panel.id}`}
                          </p>
                          <h2 className="mt-1 text-lg font-bold text-custom-blue leading-snug line-clamp-2 group-hover:text-custom-light-blue transition-colors">
                            {title}
                          </h2>
                        </div>
                        <Badge variant={getStatusVariant(status)} size="sm">
                          {String(status).replace(/_/g, " ")}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {panel.panelType && (
                          <span className="inline-flex items-center rounded-md bg-srp-foam px-2.5 py-1 text-xs font-medium text-custom-blue">
                            {panel.panelType}
                          </span>
                        )}
                        {panel.femaRegion && (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800">
                            Region {panel.femaRegion}
                          </span>
                        )}
                        {panel.isPublished && (
                          <span className="inline-flex items-center rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
                            Published
                          </span>
                        )}
                      </div>

                      {panel.requestSummary && (
                        <p className="text-sm text-gray-700 line-clamp-2 mb-4">
                          {panel.requestSummary}
                        </p>
                      )}

                      <div className="mt-auto space-y-4">
                        {(status === "Active" || progress > 0) && (
                          <div>
                            <div className="mb-1 flex items-center justify-between text-xs font-medium text-gray-700">
                              <span>Progress</span>
                              <span>{progress}%</span>
                            </div>
                            <ProgressBar
                              value={progress}
                              max={100}
                              label="Progress"
                              showLabel={false}
                            />
                          </div>
                        )}

                        <dl className="grid grid-cols-2 gap-3 text-sm">
                          <div className="rounded-lg bg-gray-50 px-3 py-2">
                            <dt className="text-xs text-gray-600">Created</dt>
                            <dd className="font-semibold text-gray-900">
                              {formatDate(panel.createdAt)}
                            </dd>
                          </div>
                          <div className="rounded-lg bg-gray-50 px-3 py-2">
                            <dt className="text-xs text-gray-600">Deadline</dt>
                            <dd className="font-semibold text-gray-900">
                              {formatDate(panel.deadline)}
                            </dd>
                          </div>
                        </dl>

                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <p className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                            <svg
                              className="w-4 h-4 text-custom-light-blue"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                            {members} member{members === 1 ? "" : "s"}
                          </p>
                          <span className="text-sm font-semibold text-custom-blue group-hover:text-custom-light-blue">
                            View details →
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState
            icon={
              <svg
                className="w-16 h-16 mx-auto text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
            title="No panels found"
            message={
              searchTerm || statusFilter !== "ALL"
                ? "Try adjusting your search or filter criteria"
                : "No panels are currently available"
            }
            action={
              (searchTerm || statusFilter !== "ALL") && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("ALL");
                  }}
                >
                  Clear filters
                </Button>
              )
            }
          />
        )}
      </div>
    </div>
  );
};

export default PanelsList;
